import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import AddDoctorDialog from "@/components/admin/AddDoctorDialog";
import { FilterSearchBar, FilterChipRow } from "@/components/doctor/FilterBar";
import {
    MapPin, Star, Calendar, ClipboardText, ListBullets, Users,
    SquaresFour, Stethoscope, CheckCircle, ShieldWarning, ShieldCheck, X,
    ArrowRight, Sparkle, Plus, Warning, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import DoctorVerifyList from "@/components/admin/DoctorVerifyList";
import AdminRowActions from "@/components/admin/AdminRowActions";

/* ===========================================================================
   AdminDoctors — list view (Path B Phase A, rebuilt 2026-02-25)
   ---------------------------------------------------------------------------
   The list is now scan-and-search-only. Every per-doctor workflow (edit
   profile, manage clinic staff, manage patients, view application paper-trail,
   view billing, audit log, decommission) lives at /admin/doctors/:id.
   Row click → profile. Inline Toggle-active stays for fast operator actions.
   =========================================================================== */

const VIEW_KEY = "admin-doctors-view";
const readPersistedView = () => {
    try { return window.localStorage.getItem(VIEW_KEY) || "list"; }
    catch { return "list"; }
};

const initials = (n) => (n || "?").replace(/^Dr\.\s*/, "").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

/* iter100.66 — Data-health gap filter map. Each entry matches the same
   check the backend uses in `/admin/doctor-data-health`. Keys mirror the
   response's `checks` object so tiles on the admin overview can deep-link
   here via `?gap=<key>`.

   Predicates receive a doctor doc from `/admin/doctors` and return true
   when the doctor is *missing* that specific field. */
const GAP_FILTERS = {
    no_photo:              { label: "No photo",             pred: (d) => !((d.image || "").trim()) },
    no_bio:                { label: "No bio",               pred: (d) => !((d.bio || "").trim()) },
    no_specialization:     { label: "No specialization",    pred: (d) => !((d.specialization || "").trim()) },
    no_qualifications:     { label: "No qualifications",    pred: (d) => !((d.qualifications || "").trim()) },
    no_consultation_fee:   { label: "No consultation fee",  pred: (d) => d.consultation_fee == null || d.consultation_fee === "" },
    no_languages:          { label: "No languages",         pred: (d) => !(Array.isArray(d.languages) && d.languages.length > 0) },
    no_business_hours:     { label: "No business hours",    pred: (d) => !d.business_hours || Object.keys(d.business_hours).length === 0 },
    partial_business_hours:{
        label: "Partial hours",
        pred: (d) => {
            const bh = d.business_hours || {};
            if (Object.keys(bh).length === 0) return false;
            const days = ["mon","tue","wed","thu","fri","sat","sun"];
            return days.some((k) => {
                const row = bh[k];
                if (row === undefined || row === null) return false;
                if (typeof row !== "object") return true;
                if (row.closed === true) return false;
                return !(row.open && row.close);
            });
        },
    },
    no_landing_slug:       { label: "No landing slug",      pred: (d) => !((d.landing_slug || "").trim()) },
    no_clinic_name:        {
        label: "No clinic name",
        pred: (d) => {
            // iter100.66 fix — `primary_clinic` isn't a stored field on
            // the doctor doc; it's synthesized on the public microsite
            // from either a `practice_locations` row OR the flat
            // `clinic_name` field. `/admin/doctors` returns the flat
            // field so this predicate only checks that. If a doctor has
            // clinic_name blank but a practice_locations row filled in,
            // the backend counter (which does the join) will be correct
            // and this frontend filter will show a superset — the
            // pill's own "of N providers" hint stays honest either way.
            const flat = (d.clinic_name || "").trim();
            const nested = (d.primary_clinic && d.primary_clinic.clinic_name) || "";
            return !(flat || nested.trim());
        },
    },
    not_onboarded:         { label: "Not onboarded",        pred: (d) => !d.onboarded_at },
};


export default function AdminDoctors() {
    const [items, setItems] = useState([]);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [view, setView] = useState(readPersistedView);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [pendingCount, setPendingCount] = useState(null);
    const navigate = useNavigate();
    // iter100.66 — Deep-link filter from Data Health widget on
    // /admin overview. `?gap=no_photo` shows only doctors missing that
    // field. The pill indicator is dismissible (clears the query param).
    const [searchParams, setSearchParams] = useSearchParams();
    const gap = searchParams.get("gap");
    const gapCfg = gap && GAP_FILTERS[gap] ? GAP_FILTERS[gap] : null;

    // iter100.262 — Deep-linkable filter from the "Verify Doctors" queue
    // legacy route: `/admin/doctors?filter=pending` opens the pending
    // verification list inline.
    useEffect(() => {
        const paramFilter = searchParams.get("filter");
        if (paramFilter && ["all", "active", "inactive", "hascases", "pending", "onboarded"].includes(paramFilter)) {
            setFilter(paramFilter);
        }
    }, [searchParams]);
    // iter101.31 — Split page into two tabs: Overview (the list surface
    // admins land here to work on) + Data health (the compliance/gap
    // widget lifted out of the header card so the base page stays lean).
    // ?tab= URL param for deep-linking.
    const VALID_TABS = ["overview", "data_health"];
    const rawTab = searchParams.get("tab");
    const activeTab = VALID_TABS.includes(rawTab) ? rawTab : "overview";
    const setActiveTab = (t) => {
        const next = new URLSearchParams(searchParams);
        if (t === "overview") next.delete("tab");
        else next.set("tab", t);
        setSearchParams(next, { replace: true });
    };

    // iter100.66 — Load the same data-health payload the Overview widget
    // uses. Feeds both the Data health tab AND the "At risk" KPI tile.
    const [dataHealth, setDataHealth] = useState(null);

    const load = () => {
        setLoading(true);
        api.get("/admin/doctors").then((r) => setItems(r.data)).finally(() => setLoading(false));
    };
    useEffect(() => {
        load();
        api.get("/admin/doctor-data-health").then((r) => setDataHealth(r.data)).catch(() => {});
    }, []);

    const stats = useMemo(() => {
        const total = items.length;
        let active = 0, inactive = 0, totalCases = 0, pending = 0;
        items.forEach((d) => {
            if (d.is_active) active += 1; else inactive += 1;
            totalCases += d.cases_count || 0;
            if (d.lifecycle_status === "pending") pending += 1;
        });
        // Prefer the source-of-truth count from DoctorVerifyList (via
        // /admin/doctors/lifecycle/pending) when we have it — the list
        // endpoint applies its own filters that may differ subtly.
        return { total, active, inactive, totalCases, pending: pendingCount ?? pending };
    }, [items, pendingCount]);

    const filtered = useMemo(() => {
        let list = items;
        if (filter === "active") list = list.filter((d) => d.is_active);
        else if (filter === "inactive") list = list.filter((d) => !d.is_active);
        else if (filter === "onboarded") list = list.filter((d) => (d.lifecycle_status || "onboarded") === "onboarded");
        else if (filter === "hascases") list = list.filter((d) => (d.cases_count || 0) > 0);
        else if (filter === "pending") list = list.filter((d) => d.lifecycle_status === "pending");
        if (gapCfg) list = list.filter(gapCfg.pred);
        if (q.trim()) {
            const l = q.trim().toLowerCase();
            list = list.filter((d) =>
                (d.name || "").toLowerCase().includes(l) ||
                (d.clinic_name || "").toLowerCase().includes(l) ||
                (d.city || "").toLowerCase().includes(l) ||
                (d.specialization || "").toLowerCase().includes(l)
            );
        }
        return list;
    }, [items, q, filter, gapCfg]);

    const clearGap = () => {
        const next = new URLSearchParams(searchParams);
        next.delete("gap");
        setSearchParams(next, { replace: true });
    };
    const toggleGap = (key) => {
        // Same chip clicked twice → clear. Different chip → switch.
        const next = new URLSearchParams(searchParams);
        if (gap === key) next.delete("gap");
        else next.set("gap", key);
        setSearchParams(next, { replace: true });
    };

    const setViewPersisted = (v) => {
        try { window.localStorage.setItem(VIEW_KEY, v); } catch { /* private mode */ }
        setView(v);
    };

    return (
        <div data-testid="admin-doctors-page" className="space-y-6">
            {/* HEADER — iter101.31 · Universal admin page grammar
                (text-2xl H1 + text-xs subtitle · no hero card wrapper).
                Actions on the right, tabs below. Data-health widget moved
                to its own tab so this page stays focused on the list. */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Work · Providers</div>
                    <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">Providers</h1>
                    <p className="text-xs text-muted-foreground mt-1">
                        Every partner orthodontist across the network. Click any row to open the full profile.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                            if (!window.confirm(
                                "Remove any placeholder `randomuser.me` portraits from doctor + user records?\n\n" +
                                "Only fake URLs are stripped — real uploaded photos are untouched. Safe to run any number of times."
                            )) return;
                            try {
                                const { data } = await api.post("/admin/doctors/clear-fake-avatars");
                                const total = (data.doctors_cleared || 0) + (data.users_cleared || 0);
                                toast.success(
                                    total === 0
                                        ? "Nothing to clean — no fake portraits found."
                                        : `Removed fake portraits from ${data.doctors_cleared} doctor(s) and ${data.users_cleared} user(s).`,
                                    { description: total === 0 ? undefined : "Affected records now show the initials chip until a real photo is uploaded." }
                                );
                                load();
                            } catch (err) {
                                toast.error("Clean-up failed", { description: err?.response?.data?.detail || err?.message });
                            }
                        }}
                        className="rounded-full"
                        data-testid="admin-doctors-clear-fake-avatars-btn"
                        title="Strip legacy stock-photo URLs (randomuser.me) that got seeded into doctor/user records during earlier demo runs. Real uploaded photos are untouched."
                    >
                        Clear fake avatars
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                            if (!window.confirm(
                                "Assign a landing_slug to every doctor that doesn't already have one?\n\n" +
                                "This is safe to run any number of times — doctors who already have a slug are untouched."
                            )) return;
                            try {
                                const { data } = await api.post("/admin/doctors/backfill-slugs");
                                toast.success(
                                    data.assigned === 0
                                        ? "All doctors already have a slug — nothing to fix."
                                        : `Assigned slugs to ${data.assigned} doctor(s).`,
                                    { description: `${data.total_scanned} providers scanned.` }
                                );
                                load();
                            } catch (err) {
                                toast.error("Backfill failed", { description: err?.response?.data?.detail || err?.message });
                            }
                        }}
                        className="rounded-full"
                        data-testid="admin-doctors-backfill-slugs-btn"
                        title="One-click fix for any doctor whose Find-a-Doctor card is misrouting to /book/{id} because they were created before the auto-slug fix shipped."
                    >
                        Fix slug-less
                    </Button>
                    <Button
                        onClick={() => setAddOpen(true)}
                        className="rounded-full"
                        size="sm"
                        data-testid="admin-doctors-add-btn"
                    >
                        <Plus size={14} weight="bold" className="mr-1.5" />
                        Add provider
                    </Button>
                </div>
            </div>

            {/* TABS — Overview (default list surface) + Data health
                (compliance/gap widget). Data-health tab surfaces the full
                chip strip that used to clutter the header card. */}
            <div className="border-b border-border" data-testid="admin-doctors-tabs">
                <nav className="flex gap-6">
                    {[
                        { id: "overview",    label: "Overview",    note: "Roster · Filters · Search" },
                        { id: "data_health", label: "Data health", note: `Gaps · ${dataHealth?.total_doctors || 0} providers scanned` },
                    ].map((t) => {
                        const isActive = activeTab === t.id;
                        return (
                            <button
                                key={t.id}
                                onClick={() => setActiveTab(t.id)}
                                data-testid={`admin-doctors-tab-${t.id}`}
                                className={`relative pb-3 pt-1 text-left transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                            >
                                <div className="text-[13px] font-bold">{t.label}</div>
                                <div className="text-[10.5px] text-muted-foreground/80 mt-0.5">{t.note}</div>
                                {isActive && <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-primary rounded-t" />}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {activeTab === "data_health" ? (
                <DataHealthTab
                    dataHealth={dataHealth}
                    gap={gap}
                    onToggleGap={toggleGap}
                    onJumpToList={(k) => {
                        // iter101.32 bug-fix — toggleGap and setActiveTab both read
                        // `searchParams` from the closure. Calling them sequentially
                        // causes the second setter to overwrite the first (React
                        // batches the state updates but each reads the stale snapshot).
                        // Solution: mutate both params in ONE setSearchParams call so
                        // the gap filter survives the tab switch.
                        const next = new URLSearchParams(searchParams);
                        if (gap === k) next.delete("gap");
                        else next.set("gap", k);
                        next.delete("tab"); // jump back to Overview (default tab)
                        setSearchParams(next, { replace: true });
                    }}
                />
            ) : (<>
            {/* KPI STRIP — 5 hero tiles matching /admin/patients cockpit.
                Total · Onboarded · Active · At risk · Suspended per user
                direction. Each tile drills into its filter and syncs the
                active-tile ring so admins always know which slice they're
                looking at. */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                <StatTile
                    label="Total providers"
                    value={stats.total}
                    Icon={Stethoscope}
                    onClick={() => { setFilter("all"); clearGap(); }}
                    active={filter === "all" && !gap}
                    testid="admin-doctors-tile-total"
                />
                <StatTile
                    label="Onboarded"
                    value={items.filter((d) => (d.lifecycle_status || "onboarded") === "onboarded").length}
                    Icon={CheckCircle}
                    tone="emerald"
                    hint="Setup wizard complete"
                    onClick={() => setFilter("onboarded")}
                    active={filter === "onboarded"}
                    testid="admin-doctors-tile-onboarded"
                />
                <StatTile
                    label="Active"
                    value={stats.active}
                    Icon={CheckCircle}
                    tone="primary"
                    hint="Currently accepting cases"
                    onClick={() => setFilter("active")}
                    active={filter === "active"}
                    testid="admin-doctors-tile-active"
                />
                <StatTile
                    label="At risk"
                    value={dataHealth?.total_gaps ?? Object.values(dataHealth?.checks || {}).reduce((s, v) => s + (v || 0), 0)}
                    Icon={ShieldWarning}
                    tone="amber"
                    hint="Providers with data gaps"
                    onClick={() => setActiveTab("data_health")}
                    testid="admin-doctors-tile-at-risk"
                />
                <StatTile
                    label="Suspended"
                    value={stats.inactive}
                    Icon={ShieldWarning}
                    tone="rose"
                    hint="Deactivated or held"
                    onClick={() => setFilter("inactive")}
                    active={filter === "inactive"}
                    testid="admin-doctors-tile-suspended"
                />
            </div>

            {/* FILTER STRIP — unified with Patients / Appointments /
                Cases grammar. Search on left, view-toggle in the right
                slot, FilterChipRow below with all buckets + counts. */}
            <div className="flex flex-col gap-3" data-testid="admin-doctors-filter-bar">
                <FilterSearchBar
                    query={q}
                    setQuery={setQ}
                    placeholder="Search name, clinic, city, specialization…"
                    searchTestid="admin-doctors-search"
                    testid="admin-doctors-filter-search"
                    rightSlot={(
                        <div className="inline-flex items-center rounded-md border border-border bg-card overflow-hidden h-9">
                            <button
                                type="button" onClick={() => setViewPersisted("list")}
                                className={`px-2.5 h-full inline-flex items-center justify-center transition ${view === "list" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                data-testid="admin-doctors-view-list"
                                aria-pressed={view === "list"}
                                title="List view"
                            >
                                <ListBullets size={13} weight="bold" />
                            </button>
                            <button
                                type="button" onClick={() => setViewPersisted("grid")}
                                className={`px-2.5 h-full inline-flex items-center justify-center transition ${view === "grid" ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-secondary"}`}
                                data-testid="admin-doctors-view-grid"
                                aria-pressed={view === "grid"}
                                title="Grid view"
                            >
                                <SquaresFour size={13} weight="bold" />
                            </button>
                        </div>
                    )}
                />
                <FilterChipRow
                    chips={[
                        { id: "all",       label: "All providers",   count: items.length },
                        { id: "onboarded", label: "Onboarded",       count: items.filter((d) => (d.lifecycle_status || "onboarded") === "onboarded").length, tone: "emerald" },
                        { id: "active",    label: "Active",          count: stats.active, tone: "primary" },
                        { id: "inactive",  label: "Suspended",       count: stats.inactive, tone: "rose" },
                        { id: "hascases",  label: "Has cases",       count: items.filter((d) => (d.cases_count || 0) > 0).length, tone: "amber" },
                        { id: "pending",   label: "Pending verification", count: stats.pending, tone: "amber" },
                    ]}
                    value={filter}
                    onChange={(v) => { setFilter(v); clearGap(); }}
                    testid="admin-doctors-chip-row"
                    chipTestidPrefix="admin-doctors-pill"
                />
            </div>

            {/* iter100.66 — Active gap-filter pill from Data Health widget. */}
            {gapCfg && (
                <div
                    className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 flex items-center gap-2 flex-wrap"
                    data-testid="admin-doctors-gap-filter-pill"
                >
                    <Warning size={16} weight="duotone" className="text-amber-700 shrink-0" />
                    <span className="text-sm">
                        <span className="text-muted-foreground">Filtering by data-health gap:</span>{" "}
                        <strong className="font-semibold text-amber-800">{gapCfg.label}</strong>
                        <span className="text-muted-foreground"> · {filtered.length} of {items.length} providers</span>
                    </span>
                    <button
                        type="button"
                        onClick={clearGap}
                        className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:text-amber-900 h-7 px-2.5 rounded-full border border-amber-300 bg-white/60 hover:bg-white transition-colors"
                        data-testid="admin-doctors-gap-filter-clear"
                    >
                        <X size={12} weight="bold" />
                        Clear
                    </button>
                </div>
            )}

            {loading ? (
                <div className="text-muted-foreground text-sm text-center py-10">Loading providers…</div>
            ) : filter === "pending" ? (
                <DoctorVerifyList onQueueChange={setPendingCount} />
            ) : filtered.length === 0 ? (
                <EmptyState total={items.length} isFiltered={q.length > 0 || filter !== "all"} onClear={() => { setQ(""); setFilter("all"); }} onAdd={() => setAddOpen(true)} />
            ) : view === "list" ? (
                <ListView rows={filtered} onChanged={load} />
            ) : (
                <GridView rows={filtered} onChanged={load} />
            )}
            </>)}

            <AddDoctorDialog
                open={addOpen}
                onOpenChange={setAddOpen}
                onCreated={(newId) => {
                    load();
                    // Navigate straight into the profile so admin can finish
                    // setup (add clinic staff, edit credentials, etc.)
                    navigate(`/admin/doctors/${newId}`);
                }}
            />
        </div>
    );
}


function ListView({ rows, onChanged }) {
    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="admin-doctors-list-view">
            <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-secondary/30">
                <div className="col-span-4">Provider</div>
                <div className="col-span-3">Clinic / City</div>
                <div className="col-span-1 text-right">Patients</div>
                <div className="col-span-1 text-right">Cases</div>
                <div className="col-span-3 text-right">Action</div>
            </div>
            {rows.map((d) => (
                <Link
                    key={d.id}
                    to={`/admin/doctors/${d.id}`}
                    className={`grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 md:py-2.5 border-b border-border last:border-0 hover:bg-secondary/30 transition items-center ${!d.is_active ? "opacity-60" : ""}`}
                    data-testid={`admin-doctor-${d.id}`}
                >
                    <div className="md:col-span-4 flex items-center gap-3 min-w-0">
                        <DoctorAvatar d={d} className="w-11 h-11" />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-semibold truncate">{d.name}</div>
                                {d.is_new_partner && !d.rating && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase font-bold tracking-wider inline-flex items-center gap-1">
                                        <Sparkle size={9} weight="fill" /> New
                                    </span>
                                )}
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate">{d.specialization || "—"}</div>
                        </div>
                    </div>
                    <div className="hidden md:flex md:col-span-3 flex-col min-w-0 text-xs text-muted-foreground">
                        <span className="truncate font-medium text-foreground">{d.clinic_name || "—"}</span>
                        <span className="flex items-center gap-1 truncate mt-0.5"><MapPin size={11} /> {d.city || "—"}</span>
                    </div>
                    <div className="md:col-span-1 flex md:justify-end items-center gap-1 text-xs tabular-nums text-foreground font-medium">
                        <Users size={11} className="md:hidden text-muted-foreground" />
                        <span>{d.patients_count ?? 0}</span>
                    </div>
                    <div className="md:col-span-1 flex md:justify-end items-center gap-1 text-xs tabular-nums text-foreground font-medium">
                        <ClipboardText size={11} className="md:hidden text-muted-foreground" />
                        <span>{d.cases_count ?? 0}</span>
                    </div>
                    <div className="md:col-span-3 flex md:justify-end items-center gap-2">
                        <AdminRowActions
                            kind="doctor"
                            targetId={d.id}
                            targetUserId={d.user_id}
                            targetName={d.name}
                            isActive={d.is_active !== false}
                            onChanged={onChanged}
                        />
                    </div>
                </Link>
            ))}
        </div>
    );
}


function GridView({ rows, onChanged }) {
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-doctors-grid-view">
            {rows.map((d) => (
                <Link
                    key={d.id}
                    to={`/admin/doctors/${d.id}`}
                    className={`block bg-card border border-border rounded-xl p-4 hover:border-primary/40 hover:shadow-sm transition ${!d.is_active ? "opacity-60" : ""}`}
                    data-testid={`admin-doctor-${d.id}`}
                >
                    <div className="flex items-start gap-3">
                        <DoctorAvatar d={d} className="w-14 h-14" />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-display font-semibold truncate">{d.name}</div>
                                {!d.is_active && <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 uppercase font-bold tracking-wider">Inactive</span>}
                                {d.is_new_partner && !d.rating && (
                                    <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 uppercase font-bold tracking-wider inline-flex items-center gap-1">
                                        <Sparkle size={9} weight="fill" /> New
                                    </span>
                                )}
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate">{d.specialization || "—"}</div>
                            <div className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5 truncate">
                                <MapPin size={10} /> {d.clinic_name || "—"} · {d.city || "—"}
                            </div>
                        </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-border flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1"><Star size={11} weight="fill" className="text-amber-500" /> {d.rating || "—"}</span>
                        <span className="flex items-center gap-1"><ClipboardText size={11} /> {d.cases_count ?? 0} cases</span>
                        <span className="flex items-center gap-1"><Calendar size={11} /> {d.appointments_count ?? 0} visits</span>
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-semibold text-primary inline-flex items-center gap-1">
                            View profile <ArrowRight size={11} />
                        </span>
                        <AdminRowActions
                            kind="doctor"
                            targetId={d.id}
                            targetUserId={d.user_id}
                            targetName={d.name}
                            isActive={d.is_active !== false}
                            onChanged={onChanged}
                        />
                    </div>
                </Link>
            ))}
        </div>
    );
}


/* iter101.46 — Legacy ImpersonateBtn removed. Both list + grid views
   now use the unified `<AdminRowActions kind="doctor" />` cluster that
   renders Enable/Disable + Impersonate consistently across every admin
   surface (Team & Access, Doctors, Patients). */


function EmptyState({ total, isFiltered, onClear, onAdd }) {
    if (isFiltered && total > 0) {
        return (
            <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="admin-doctors-empty">
                <ClipboardText size={28} className="mx-auto text-muted-foreground mb-3" />
                <div className="font-display text-lg font-bold">No providers match your filters</div>
                <p className="text-xs text-muted-foreground mt-1.5">Try clearing your search or switching to a different filter.</p>
                <Button onClick={onClear} variant="outline" className="rounded-full mt-4" data-testid="admin-doctors-empty-clear">Clear filters</Button>
            </div>
        );
    }
    return (
        <div className="bg-card border border-dashed border-border rounded-2xl p-12 text-center" data-testid="admin-doctors-empty">
            <Stethoscope size={28} className="mx-auto text-muted-foreground mb-3" />
            <div className="font-display text-lg font-bold">No providers yet</div>
            <p className="text-xs text-muted-foreground mt-1.5 max-w-md mx-auto">
                Approve a provider application from the Applications page, or add a provider manually below.
            </p>
            <Button onClick={onAdd} className="rounded-full mt-4">
                <Plus size={13} weight="bold" className="mr-1.5" /> Add a provider
            </Button>
        </div>
    );
}


function StatTile({ label, value, Icon, tone, hint, onClick, active, testid }) {
    /* iter101.32 — Canonical StatTile matching Patients/AdminReferrals/
       ScanVisits (items-start, label above value, bg-card border, active
       ring). Previously used items-center + swapped label/value order —
       looked visually different from every other Work-tab tile. */
    const accent = tone === "emerald" ? "text-emerald-700 bg-emerald-100"
        : tone === "amber" ? "text-amber-700 bg-amber-100"
        : tone === "rose" ? "text-rose-700 bg-rose-100"
        : tone === "violet" ? "text-violet-700 bg-violet-100"
        : "text-primary bg-primary/10";
    const Component = onClick ? "button" : "div";
    return (
        <Component
            type={onClick ? "button" : undefined}
            onClick={onClick}
            className={`flex items-start gap-3 rounded-xl bg-card border p-3 text-left transition ${
                active ? "border-primary ring-1 ring-primary/30 bg-primary/5"
                       : (onClick ? "border-border hover:border-primary/40 hover:shadow-sm cursor-pointer" : "border-border")
            }`}
            aria-pressed={onClick ? active : undefined}
            data-testid={testid}
        >
            <div className={`w-9 h-9 rounded-full ${accent} grid place-items-center shrink-0`}>
                <Icon size={15} weight="fill" />
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
                <div className="text-xl font-display font-bold tabular-nums leading-none mt-1">{value}</div>
                {hint && <div className="text-[10px] text-muted-foreground mt-1 truncate">{hint}</div>}
            </div>
        </Component>
    );
}

function DoctorAvatar({ d, className = "w-11 h-11" }) {
    return d.image ? (
        <img src={d.image} alt={d.name} className={`${className} rounded-full object-cover bg-secondary shrink-0`} onError={(e) => { e.target.style.display = "none"; }} />
    ) : (
        <div className={`${className} rounded-full bg-primary/10 text-primary font-semibold grid place-items-center shrink-0 text-sm`}>
            {initials(d.name)}
        </div>
    );
}

/* iter101.31 — Dedicated Data health tab. Replaces the chip strip that
   used to clutter the top of the Providers header card. Same amber-tinted
   compliance surface — one chip per gap type, sorted by count descending.
   Clicking a chip drops back onto the Overview tab with the gap filter
   pre-applied so admins can see the affected providers straight away. */
function DataHealthTab({ dataHealth, gap, onToggleGap, onJumpToList }) {
    if (!dataHealth || !dataHealth.checks || (dataHealth.total_doctors || 0) === 0) {
        return (
            <div className="rounded-2xl border border-border bg-card p-10 text-center" data-testid="admin-doctors-data-health-empty">
                <ShieldCheck size={28} className="mx-auto text-muted-foreground mb-2" />
                <div className="font-display text-lg font-bold">Loading data-health…</div>
                <p className="text-xs text-muted-foreground mt-1">Once providers are onboarded, coverage gaps show here.</p>
            </div>
        );
    }
    const HEALTH_LABELS = {
        no_photo:              { label: "No photo",             hint: "Public listing shows placeholder avatar" },
        no_bio:                { label: "No bio",               hint: "\"About Dr. X\" section renders empty" },
        no_business_hours:     { label: "No hours",             hint: "Microsite shows \"Hours by appointment\"" },
        partial_business_hours:{ label: "Partial hours",        hint: "Some days missing open/close" },
        no_consultation_fee:   { label: "No fee",               hint: "Sidebar shows blank fee — kills trust" },
        no_specialization:     { label: "No specialization",    hint: "Search filters can't match this doctor" },
        no_qualifications:     { label: "No qualifications",    hint: "Hero credentials line hidden" },
        no_languages:          { label: "No languages",         hint: "Multilingual chip hidden" },
        no_landing_slug:       { label: "No slug",              hint: "Public microsite URL unavailable" },
        no_clinic_name:        { label: "No clinic",            hint: "\"Consult at\" sidebar blank" },
        not_onboarded:         { label: "Not onboarded",        hint: "Never completed the setup wizard" },
    };
    const total = dataHealth.total_doctors;
    const rows = Object.entries(HEALTH_LABELS).map(([k, meta]) => ({
        k,
        label: meta.label,
        hint: meta.hint,
        n: dataHealth.checks[k] ?? 0,
    })).sort((a, b) => b.n - a.n);
    const totalGaps = rows.reduce((s, r) => s + r.n, 0);

    return (
        <div className="space-y-4" data-testid="admin-doctors-data-health">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Coverage gaps</div>
                    <div className="text-sm mt-0.5">
                        <span className={`font-semibold ${totalGaps > 0 ? "text-amber-700" : "text-emerald-700"} tabular-nums`}>
                            {totalGaps > 0 ? `${totalGaps} coverage gap${totalGaps === 1 ? "" : "s"}` : "No gaps — all clean"}
                        </span>
                        <span className="text-muted-foreground"> across {total} provider{total === 1 ? "" : "s"}</span>
                    </div>
                </div>
                {gap && (
                    <button
                        type="button"
                        onClick={() => onToggleGap(gap)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:text-amber-900 h-7 px-2.5 rounded-full border border-amber-300 bg-amber-50 hover:bg-white transition-colors"
                        data-testid="admin-doctors-data-health-clear"
                    >
                        <X size={12} weight="bold" /> Clear filter
                    </button>
                )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {rows.map((r) => {
                    const hasWork = r.n > 0;
                    const pct = total > 0 ? Math.round((r.n / total) * 100) : 0;
                    const isActive = gap === r.k;
                    const cls = `p-3 rounded-xl border transition text-left ${
                        isActive ? "border-amber-500 bg-amber-100" :
                        hasWork ? "border-border bg-card hover:bg-amber-50/60 hover:border-amber-300" :
                                  "border-border bg-secondary/40 opacity-70"
                    }`;
                    return (
                        <button
                            key={r.k}
                            type="button"
                            onClick={() => hasWork && onJumpToList(r.k)}
                            disabled={!hasWork}
                            className={cls + (hasWork ? " cursor-pointer" : " cursor-default")}
                            data-testid={`admin-doctors-data-health-${r.k}`}
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground truncate">{r.label}</div>
                                <span className={`min-w-[32px] h-6 px-2 rounded-full text-xs font-bold tabular-nums grid place-items-center ${hasWork ? "bg-amber-500 text-white" : "bg-secondary text-muted-foreground"}`}>
                                    {r.n}
                                </span>
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{r.hint}</div>
                            {hasWork && (
                                <div className="text-[10px] text-amber-700 mt-1 font-semibold">{pct}% of network</div>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

